import React, {useMemo, useState} from 'react';

import {Banner, Button, Icon, Tab} from '@albert-io/atomic';
import ManageRosterContext from 'client/Classrooms/Classroom/Teacher/ManageRoster/ManageRoster.context';
import manageRosterModalCollection from 'client/Classrooms/Classroom/Teacher/ManageRoster/manageRosterModalCollection';
import PeopleContext from 'client/Classrooms/Classroom/Teacher/People/people.context';
import LoadingIndicator from 'generic/LoadingIndicator.react';

import {StudentsTab} from './StudentsTab';
import {TeachersTab} from './TeachersTab';
import {usePeopleRoster} from './usePeopleRoster';

type PeopleTab = 'students' | 'teachers';

// The reused legacy roster modals/dropdowns read these untyped contexts.
const TypedManageRosterContext = ManageRosterContext as unknown as React.Context<unknown>;
const TypedPeopleContext = PeopleContext as unknown as React.Context<unknown>;
// email | bulkResend | classCode | googleClassroom | LMS — student invite flows, rendered on demand.
const ROSTER_MODALS = manageRosterModalCollection as Record<
  string,
  React.ComponentType<{handleClose: () => void}>
>;

interface Props {
  classroomId: string;
}

export const People = ({classroomId}: Props) => {
  const roster = usePeopleRoster(classroomId);
  const [activeTab, setActiveTab] = useState<PeopleTab>('students');
  // Drives the shared student-invite modals (mirrors the legacy ManageRoster container).
  const [modalToShow, setModalToShow] = useState('');

  const {classroom, isReady, hasError, reload, queries, currentTeacherId, students, invitations} =
    roster;

  // setIsReady(false) is how the reused flows signal "I changed data" — turn it into a refetch.
  const manageRosterContext = useMemo(
    () => ({
      queries: {
        classroom: queries.classroom,
        invitations: queries.invitations,
        students: queries.students
      },
      classroom,
      invitations,
      students,
      isReady,
      modalToShow,
      setIsReady: (value: boolean) => {
        if (!value) {
          reload();
        }
      },
      setModalToShow
    }),
    [queries, classroom, invitations, students, isReady, modalToShow, reload]
  );

  const peopleContext = useMemo(
    () => ({classroom, queries: {coteachers: queries.coteachers}, currentTeacherId}),
    [classroom, queries, currentTeacherId]
  );

  if (!isReady) {
    return <LoadingIndicator />;
  }

  if (hasError || typeof classroom?.getId !== 'function') {
    return (
      <div className='pt-x4'>
        <Banner type='negative'>
          <Banner.Icon icon='exclamation' />
          <div className='flex flex-col items-start gap-x1'>
            <span>We couldn’t load this class’s roster.</span>
            <Button variant='outlined' color='secondary' onClick={reload}>
              Try again
            </Button>
          </div>
        </Banner>
      </div>
    );
  }

  const ModalRenderer = modalToShow ? ROSTER_MODALS[modalToShow] : null;

  return (
    <TypedManageRosterContext.Provider value={manageRosterContext}>
      <TypedPeopleContext.Provider value={peopleContext}>
        <div className='flex flex-col'>
          <nav
            aria-label='People views'
            className='-mx-x4 flex border-b border-border-primary px-x4'
          >
            <Tab
              as='button'
              isActive={activeTab === 'students'}
              onClick={() => setActiveTab('students')}
            >
              <Icon className='mr-x1' icon='users' iconStyle='regular' size='inherit' />
              Students
            </Tab>
            <Tab
              as='button'
              isActive={activeTab === 'teachers'}
              onClick={() => setActiveTab('teachers')}
            >
              <Icon className='mr-x1' icon='person-chalkboard' iconStyle='regular' size='inherit' />
              Teachers
            </Tab>
          </nav>
          {activeTab === 'students' ? (
            <StudentsTab
              classroom={classroom}
              invitations={invitations}
              students={students}
              onShowModal={setModalToShow}
            />
          ) : (
            <TeachersTab coteachers={roster.coteachers} currentTeacherId={currentTeacherId} />
          )}
        </div>
        {ModalRenderer && <ModalRenderer handleClose={() => setModalToShow('')} />}
      </TypedPeopleContext.Provider>
    </TypedManageRosterContext.Provider>
  );
};
